/**
 * Offline Storage Manager
 * Manages IndexedDB for storing offline data and pending changes
 */

export interface OfflineData {
  id: string;
  type: 'website_update' | 'image_upload' | 'content_edit' | 'pet_listing' | 'litter_update';
  data: any;
  timestamp: number;
  synced: boolean;
  retryCount: number;
}

const DB_NAME = 'pawsindia_offline';
const DB_VERSION = 1;
const STORE_NAME = 'offline_changes';

class OfflineStorageManager {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB database
   */
  async init(): Promise<void> {
    if (this.db) return;
    
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          
          // Create indexes for efficient querying
          objectStore.createIndex('synced', 'synced', { unique: false });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('type', 'type', { unique: false });
          
          console.log('Object store created with indexes');
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Save data for offline sync
   */
  async saveOffline(data: Omit<OfflineData, 'id' | 'timestamp' | 'synced' | 'retryCount'>): Promise<string> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const offlineData: OfflineData = {
      id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...data,
      timestamp: Date.now(),
      synced: false,
      retryCount: 0
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(offlineData);

      request.onsuccess = () => {
        console.log('Data saved offline:', offlineData.id);
        resolve(offlineData.id);
      };

      request.onerror = () => {
        console.error('Failed to save offline data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all unsynced data
   */
  async getUnsynced(): Promise<OfflineData[]> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('synced');
      const request = index.getAll(IDBKeyRange.only(false));

      request.onsuccess = () => {
        const results = request.result as OfflineData[];
        // Sort by timestamp (oldest first)
        results.sort((a, b) => a.timestamp - b.timestamp);
        console.log(`Found ${results.length} unsynced items`);
        resolve(results);
      };

      request.onerror = () => {
        console.error('Failed to get unsynced data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Mark a specific item as synced
   */
  async markAsSynced(id: string): Promise<void> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const data = getRequest.result as OfflineData;
        if (data) {
          data.synced = true;
          const updateRequest = store.put(data);

          updateRequest.onsuccess = () => {
            console.log('Marked as synced:', id);
            resolve();
          };

          updateRequest.onerror = () => {
            console.error('Failed to mark as synced:', updateRequest.error);
            reject(updateRequest.error);
          };
        } else {
          resolve(); // Item doesn't exist, consider it synced
        }
      };

      getRequest.onerror = () => {
        console.error('Failed to get item for syncing:', getRequest.error);
        reject(getRequest.error);
      };
    });
  }

  /**
   * Increment retry count for a failed sync attempt
   */
  async incrementRetryCount(id: string): Promise<void> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const data = getRequest.result as OfflineData;
        if (data) {
          data.retryCount += 1;
          const updateRequest = store.put(data);

          updateRequest.onsuccess = () => {
            console.log('Incremented retry count:', id, data.retryCount);
            resolve();
          };

          updateRequest.onerror = () => {
            console.error('Failed to increment retry count:', updateRequest.error);
            reject(updateRequest.error);
          };
        } else {
          resolve();
        }
      };

      getRequest.onerror = () => {
        console.error('Failed to get item for retry count:', getRequest.error);
        reject(getRequest.error);
      };
    });
  }

  /**
   * Clear all synced data
   */
  async clearSynced(): Promise<void> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('synced');
      const request = index.openCursor(IDBKeyRange.only(true));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          console.log('Cleared all synced data');
          resolve();
        }
      };

      request.onerror = () => {
        console.error('Failed to clear synced data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get all data (for debugging)
   */
  async getAll(): Promise<OfflineData[]> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result as OfflineData[]);
      };

      request.onerror = () => {
        console.error('Failed to get all data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Clear all data (for testing/reset)
   */
  async clearAll(): Promise<void> {
    await this.init();
    
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => {
        console.log('Cleared all offline data');
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to clear all data:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
      console.log('Database connection closed');
    }
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorageManager();
